import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  parseOrderingErrorBody,
  parseOrderingParam,
  type TableSortState,
  toggleSortState,
  toOrderingParam,
} from '../../utils/listOrdering';
import {
  ADMIN_ORDERS_PAGE_SIZE,
  readAdminOrdersPage,
} from './adminOrdersModel';
import {
  ADMIN_ORDER_FIELD_TO_SORT_KEY,
  ADMIN_ORDER_SORT_KEY_TO_FIELD,
  type AdminOrderSortKey,
  PA_URL_ORDERS,
} from './platformAdminOrdering';

const NO_SORT: TableSortState<AdminOrderSortKey> = {
  key: null,
  direction: 'desc',
};

/**
 * Orders (Admin) list state. Called by PlatformAdmin rather than the tab so
 * status, page and rows survive switching to another tab and back.
 */
export function useAdminOrders(active: boolean) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [status, setStatusState] = useState('All');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [listError, setListError] = useState<string | null>(null);

  const sort: TableSortState<AdminOrderSortKey> = useMemo(
    () =>
      parseOrderingParam(
        searchParams.get(PA_URL_ORDERS),
        ADMIN_ORDER_FIELD_TO_SORT_KEY,
      ) ?? NO_SORT,
    [searchParams],
  );

  const orderingApi = useMemo(
    () => toOrderingParam(sort, ADMIN_ORDER_SORT_KEY_TO_FIELD),
    [sort],
  );

  const toggleSort = useCallback(
    (key: AdminOrderSortKey) => {
      if (!ADMIN_ORDER_SORT_KEY_TO_FIELD[key]) return;
      setPage(1);
      setListError(null);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const current =
          parseOrderingParam(
            prev.get(PA_URL_ORDERS),
            ADMIN_ORDER_FIELD_TO_SORT_KEY,
          ) ?? NO_SORT;
        const toggled = toggleSortState(current, key);
        const param = toOrderingParam(toggled, ADMIN_ORDER_SORT_KEY_TO_FIELD);
        if (param) next.set(PA_URL_ORDERS, param);
        else next.delete(PA_URL_ORDERS);
        return next;
      });
    },
    [setSearchParams],
  );

  const setStatus = useCallback((next: string) => {
    setStatusState(next);
    setPage(1);
    setListError(null);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1);
    setListError(null);
  };

  const fetchOrders = useCallback(
    async (pageToLoad = 1, statusToLoad = 'All') => {
      setLoading(true);
      setListError(null);
      try {
        const response = await api.get(
          apiEndpoints.getAdminOrders(
            pageToLoad,
            ADMIN_ORDERS_PAGE_SIZE,
            statusToLoad,
            orderingApi ?? undefined,
            debouncedSearch || undefined,
          ),
        );
        if (response && response.ok) {
          const result = readAdminOrdersPage(await response.json());
          setOrders(result.orders);
          if (result.count !== undefined) setCount(result.count);
          if (result.totalPages !== undefined) {
            setTotalPages(result.totalPages);
          }
        } else if (response?.status === 400) {
          const body = await response.json().catch(() => ({}));
          setListError(parseOrderingErrorBody(body) || 'Could not apply sort.');
          setOrders([]);
          setCount(0);
          setTotalPages(1);
        } else {
          setOrders([]);
        }
      } catch {
        setOrders([]);
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, orderingApi],
  );

  useEffect(() => {
    if (active) {
      fetchOrders(page, status);
    }
  }, [active, page, status, fetchOrders]);

  return {
    orders,
    loading,
    page,
    totalPages,
    count,
    status,
    search,
    appliedSearch: debouncedSearch,
    listError,
    sort,
    setPage,
    setStatus,
    onSearchChange,
    toggleSort,
  };
}

export type AdminOrdersState = ReturnType<typeof useAdminOrders>;
